import { Box } from '@mui/material';
import { AnalyticsColors } from './analyticsUi';
import { FunnelStage, ConversionRates, ConversionSpeed as SpeedType } from 'hooks/analytics';
import { formatPercentage } from 'utils/formatters';

interface FunnelChartProps {
    funnel: FunnelStage;
    rates: ConversionRates;
    speed: SpeedType;
    comparisonRates?: ConversionRates;
    comparisonSpeed?: SpeedType;
    compact?: boolean;
    onTransitionClick?: (transitionKey: string) => void;
}

export const STAGE_COLORS = ['#2563EB', '#0891B2', '#7C3AED', '#059669'];
export const STAGE_LABELS = ['Registered', 'Assessment Done', 'Offer Sent', 'Paid'];

const STAGE_KEYS: { key: keyof FunnelStage; label: string }[] = [
    { key: 'registered', label: 'Registered' },
    { key: 'assessmentCompleted', label: 'Assessment Done' },
    { key: 'offerSent', label: 'Offer Sent' },
    { key: 'paid', label: 'Paid' },
];

const RATE_KEYS: { key: keyof ConversionRates; label: string }[] = [
    { key: 'registeredToAssessment', label: 'Conversion' },
    { key: 'assessmentToOffer', label: 'Conversion' },
    { key: 'offerToPaid', label: 'Conversion' },
];

const SPEED_KEYS: (keyof SpeedType)[] = ['avgDaysToAssessment', 'avgDaysToOffer', 'avgDaysToPaid'];

const TRANSITION_KEYS = ['registeredToAssessment', 'assessmentToOffer', 'offerToPaid'];

const FunnelChart = ({
    funnel,
    rates,
    speed,
    comparisonRates,
    comparisonSpeed,
    compact,
    onTransitionClick,
}: FunnelChartProps) => {
    const maxValue = funnel.registered || 1;

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', gap: '1.25rem', mb: '1.25rem', flexWrap: 'wrap' }}>
                {STAGE_KEYS.map((stage, i) => (
                    <Box
                        key={stage.key}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: 12,
                            fontWeight: 500,
                            color: AnalyticsColors.textSecondary,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '3px',
                                background: STAGE_COLORS[i],
                                flexShrink: 0,
                            }}
                        />
                        {stage.label}
                    </Box>
                ))}
            </Box>

            {STAGE_KEYS.map((stage, i) => {
                const value = funnel[stage.key];
                const widthPercent = (value / maxValue) * 100;
                const rateIndex = i - 1;
                const dropOff = i > 0 ? funnel[STAGE_KEYS[i - 1].key] - value : 0;
                const isClickable = !!onTransitionClick;

                return (
                    <div key={stage.key}>
                        {i > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: '0.5rem 0 0.5rem 160px',
                                    gap: '0.75rem',
                                    ...(isClickable && {
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        '&:hover': { background: '#F9FAFB' },
                                    }),
                                }}
                                onClick={() => onTransitionClick?.(TRANSITION_KEYS[rateIndex])}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        p: '3px 10px',
                                        background: AnalyticsColors.primaryLight,
                                        borderRadius: '6px',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: AnalyticsColors.primary,
                                    }}
                                >
                                    {formatPercentage(rates[RATE_KEYS[rateIndex].key])}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '12.5px',
                                        fontWeight: 500,
                                        color: AnalyticsColors.textSecondary,
                                    }}
                                >
                                    {dropOff} dropped
                                </Box>
                                <Box
                                    component="span"
                                    sx={{ color: AnalyticsColors.textTertiary, fontSize: 10 }}
                                >
                                    ·
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '12.5px',
                                        fontWeight: 500,
                                        color: AnalyticsColors.textSecondary,
                                    }}
                                >
                                    avg {speed[SPEED_KEYS[rateIndex]].toFixed(1)} days
                                </Box>
                                {comparisonRates && (
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            p: '2px 8px',
                                            borderRadius: '4px',
                                            background:
                                                rates[RATE_KEYS[rateIndex].key] >=
                                                comparisonRates[RATE_KEYS[rateIndex].key]
                                                    ? '#DCFCE7'
                                                    : '#FEE2E2',
                                            color:
                                                rates[RATE_KEYS[rateIndex].key] >=
                                                comparisonRates[RATE_KEYS[rateIndex].key]
                                                    ? '#166534'
                                                    : '#991B1B',
                                        }}
                                    >
                                        {rates[RATE_KEYS[rateIndex].key] >=
                                        comparisonRates[RATE_KEYS[rateIndex].key]
                                            ? '↑'
                                            : '↓'}{' '}
                                        {Math.abs(
                                            rates[RATE_KEYS[rateIndex].key] -
                                                comparisonRates[RATE_KEYS[rateIndex].key]
                                        ).toFixed(1)}
                                        % vs prev
                                    </Box>
                                )}
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                            <Box
                                sx={{
                                    width: 160,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: AnalyticsColors.textPrimary,
                                    flexShrink: 0,
                                    textAlign: 'right',
                                    pr: '1rem',
                                }}
                            >
                                {stage.label}
                            </Box>
                            <Box sx={{ flex: 1, position: 'relative' }}>
                                <Box
                                    sx={{
                                        height: 44,
                                        width: `${Math.max(widthPercent, 6)}%`,
                                        background: STAGE_COLORS[i],
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'width 0.5s ease',
                                        position: 'relative',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 15,
                                            fontWeight: 700,
                                            color: 'white',
                                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
                                        }}
                                    >
                                        {value}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </div>
                );
            })}
        </Box>
    );
};

export default FunnelChart;
